/* eslint-disable @shopliftr/angular/dialog-api-only-in-dialogs -- This pattern requires a MatDialog */
import {
    DestroyRef, Directive, inject
} from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { Observable } from "rxjs";
import {
    Hotkey, HotkeysService
} from "../../services/hotkeys/hotkeys.service";

/**
 * A selector-less directive that wires hotkey registration for a host element.
 *
 * This directive is not applied via a selector. Instead, it is composed into
 * components or directives via the `hostDirectives` array. It automatically
 * injects `DestroyRef` and detects whether the host is inside a `MatDialog`,
 * delegating to the appropriate dialog-scoped or page-scoped service methods.
 *
 * When the host is rendered inside a Material dialog, `addShortcut$` and
 * `addSequenceShortcut$` automatically delegate to the dialog-scoped service
 * methods so that hotkeys are properly isolated to the topmost dialog.
 *
 * @example
 * ```typescript
 * @Component({
 *   selector: "mgd-my-component",
 *   hostDirectives: [HotkeysHostDirective]
 * })
 * export class MyComponent {
 *   private readonly _hotkeys = inject(HotkeysHostDirective, { self: true });
 * }
 * ```
 *
 * @selector none — composed via `hostDirectives` only
 * @see {@link HotkeysService} for the underlying hotkey registration service
 */
@Directive({})
export class HotkeysHostDirective {
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _hotkeys = inject(HotkeysService);
    private readonly _dialogRef = inject(MatDialogRef, { optional: true });

    /**
     * Registers a single hotkey combination on the host element.
     *
     * Automatically injects the directive's `DestroyRef` so that the hotkey
     * subscription is cleaned up when the host is destroyed.
     *
     * When the host is inside a Material dialog, delegates to
     * `HotkeysService.addDialogShortcut$` so that the hotkey is scoped to
     * the dialog and suppressed when a nested dialog is open.
     *
     * @param options - Hotkey configuration. The `keys` property is required.
     * @returns Observable that emits `KeyboardEvent` each time the hotkey is triggered.
     */
    addShortcut$(options: Hotkey): Observable<KeyboardEvent> {
        const mergedOptions: Hotkey = {
            ...options,
            destroyRef: this._destroyRef
        };

        if (this._dialogRef) {
            return this._hotkeys.addDialogShortcut$(mergedOptions);
        }
        return this._hotkeys.addShortcut$(mergedOptions);
    }

    /**
     * Registers a multi-key sequence hotkey on the host element.
     *
     * Automatically injects the directive's `DestroyRef` so that the hotkey
     * subscription is cleaned up when the host is destroyed.
     *
     * When the host is inside a Material dialog, delegates to
     * `HotkeysService.addDialogSequenceShortcut$` so that the sequence is
     * scoped to the dialog and suppressed when a nested dialog is open.
     *
     * @param options - Hotkey configuration. The `keys` property is required and
     * should be a `>`-delimited sequence (e.g. `'g>h'`).
     * @returns Observable that emits a `Hotkey` object each time the sequence is completed.
     */
    addSequenceShortcut$(options: Hotkey): Observable<Hotkey> {
        const mergedOptions: Hotkey = {
            ...options,
            destroyRef: this._destroyRef
        };

        if (this._dialogRef) {
            return this._hotkeys.addDialogSequenceShortcut$(mergedOptions);
        }
        return this._hotkeys.addSequenceShortcut$(mergedOptions);
    }
}
